import React, {useState, useRef, MutableRefObject, useLayoutEffect} from 'react';
import intl from "react-intl-universal";
import {observer} from "mobx-react-lite";
import classNames from "classnames";
import {useAppState, useRootStore, useUserState} from "@stores/AppStateContext";

import { EditorView } from "prosemirror-view";
import { EditorState } from "prosemirror-state";
import type { CollabEditor } from "../../../Editor/CollabEditor";
import { ItemModelType } from "../../../stores/WorkspaceStore";
import { ItemType } from "../../../API";
import { PreventRerender } from "../../components/PreventRerender";

import { AiIcon } from "../../components/icons/AiIcon";
import { useNotification } from "../../hooks/useNotification";
import { EmptyPlaceHolder } from "./components/EmptyPlaceHolder";
import { NodeTitle } from "./components/NodeTitle";
import { Breadcrumbs } from "./components/Breadcrumbs";
import { MoreActions } from "./components/MoreActions";
import { DocumentLoadingPlaceholder } from "../../components/DocumentLoadingPlaceholder";
import { HiddenOnMobile } from "../HiddenOnMobile";
import { Button } from "../../components/Button";
import { ClusterView } from "../ClusterView";
import { AcknowledgmentRow } from "./components/AcknowledgmentRow/AcknowledgmentRow";

import { EditorViewContext, EditorStateContext } from "./provider";
import { MODALS } from "../../screens/modals/modal-names";

import "./EditorReact.css";

export interface EditorViewRef {
    current: EditorView | null;
}

export const editorViewRef: EditorViewRef = {
    current: null
};
export const editorRootRef: MutableRefObject<HTMLDivElement | null> = {
    current: null
};
export const editorContextRootRef: MutableRefObject<HTMLDivElement | null> = {
    current: null
};
export const editorScrollRootRef: MutableRefObject<HTMLDivElement | null> = {
    current: null
};

export const EditorReact = observer(() => {
    const appState = useAppState();
    const rootStore = useRootStore();
    const userState = useUserState();
    const activeWorkspace = rootStore.view.workspace!;
    const selectedNode = activeWorkspace.selected!;
    const {enqueueSnackbar} = useNotification();

    const _isReadOnly = userState!.isCurrentUserReadOnly;
    const editorNodeRef = useRef<HTMLDivElement | null>(null);
    const collabEditorRef = useRef<CollabEditor | null>(null);

    if (import.meta.env.VITE_APP_ENV !== 'production') {
        // @ts-ignore
        (window as any).collabEditorRef = collabEditorRef;
    }

    const [view, setView] = useState<EditorView | null>(null);
    const [state, setState] = useState<EditorState | null>(null);
    const [isEditorLoading, setIsEditorLoading] = useState(false);

    const initEditor = async (selectedNode: ItemModelType, abortController: AbortController, onLoad: () => void) => {
        try {
            const {CollabEditor} = await import("../../../Editor/CollabEditor");

            if (abortController.signal.aborted) {
                return;
            }
            collabEditorRef.current = new CollabEditor(
                editorNodeRef.current!,
                selectedNode.id,
                activeWorkspace.id,
                (newState: EditorState | null) => {
                    setState(newState);
                },
                (newView: EditorView | null) => {
                    setView(newView);
                    editorViewRef.current = newView;
                },
                enqueueSnackbar,
                appState,
                onLoad,
            );
            return collabEditorRef.current;
        } catch (error) {
            // Re-throw to be handled by setupEditor's catch block
            throw error;
        }
    }
    useLayoutEffect(() => {
        const abortController = new AbortController();

        let mounted = true;
        let currentEditor: CollabEditor | null = null;

        const setupEditor = async () => {
            try {
                if (selectedNode.type === ItemType.LEAF && editorNodeRef.current) {
                    setIsEditorLoading(true);

                    const collabEditor = await initEditor(selectedNode, abortController, () => {
                        if (!mounted || abortController.signal.aborted) return;
                        setIsEditorLoading(false);
                    });
                    if (!mounted || !collabEditor || abortController.signal.aborted) return;

                    currentEditor = collabEditor;
                    await collabEditor.init();
                } else {
                    // Non-leaf node
                    setIsEditorLoading(false);
                }
            } catch (err) {
                console.error("Editor creation error:", err);
                
                // Check if this is a chunk loading error
                const error = err as Error;
                const isChunkError = error?.message?.toLowerCase().includes('failed to fetch') ||
                                   error?.message?.toLowerCase().includes('unable to preload css') ||
                                   error?.message?.toLowerCase().includes('dynamically imported module');
                
                if (isChunkError) {
                    // Import and use the chunk error handler
                    import('../../../utils/errors/chunkLoadErrorHandler').then(({ handleChunkLoadError }) => {
                        handleChunkLoadError(error);
                    });
                } else {
                    // Regular error handling
                    if (!abortController.signal.aborted) {
                        collabEditorRef.current?.close();
                        setIsEditorLoading(false);
                        enqueueSnackbar({
                            message: intl.get('editor.error.load').d('Failed to load editor. Please refresh the page.'),
                            variant: 'error'
                        });
                    }
                }
            }
        };

        setupEditor();

        return () => {
            mounted = false;
            abortController.abort();
            currentEditor?.close();
        };
    }, [selectedNode.id, _isReadOnly]); // only re-init when node changes or readonly state changes

    const onGenerateAiClick = () => {
        appState.setModal({name: MODALS.QUIZ_AI, params: {
            docId: selectedNode.id
        }});
    }

    return (
        <>
            <EditorStateContext.Provider value={state}>
                <EditorViewContext.Provider value={view}>
                    <div className={'ER-wrapper'}
                         ref={editorScrollRootRef}
                    >
                        <HiddenOnMobile>
                            <div className="BreadcrumbsWithAddition">
                                <Breadcrumbs
                                    item={selectedNode}
                                />
                                <div className="BreadcrumbsAddition">
                                    {
                                        selectedNode?.isLeaf && userState!.isCurrentUserAdmin
                                            ? (
                                                <Button
                                                    className="title-small Editor__CreateQuizButton"
                                                    type={'ai'}
                                                    size={'sm'}
                                                    Icon={AiIcon}
                                                    style={"soft"}
                                                    text={intl.get('AiTestsInEditor.create.button').d('Generate Test')}
                                                    onClick={onGenerateAiClick}
                                                />
                                            )
                                            : null
                                    }
                                    <MoreActions item={selectedNode}/>
                                </div>
                            </div>
                        </HiddenOnMobile>
                        <div className={"ER-body"}>
                            <NodeTitle collabEditorViewRef={collabEditorRef} selectedNode={selectedNode}
                                       key={selectedNode.id}
                                       isReadOnly={_isReadOnly}
                            />
                            {
                                selectedNode.isLeaf
                                    ? (
                                        <div className="ER-position-group" ref={editorRootRef}>
                                            {!isEditorLoading && <EmptyPlaceHolder/>}
                                            {isEditorLoading && (
                                                <div className={"ER-editor-wrapper"}>
                                                    <DocumentLoadingPlaceholder />
                                                </div>
                                            )}
                                            <PreventRerender
                                                className={classNames({
                                                    'ER-editor-wrapper': true,
                                                    'ER-editor-wrapper--short': selectedNode.acknowledgementRequired,
                                                })}
                                                innerRef={editorNodeRef}
                                            />
                                            <AcknowledgmentRow/>
                                        </div>
                                    )
                                    : (
                                        <ClusterView item={selectedNode}/>
                                    )
                            }
                        </div>
                        <PreventRerender innerRef={editorContextRootRef} className={'ER-context-root'}/>
                    </div>
                </EditorViewContext.Provider>
            </EditorStateContext.Provider>
        </>
    )
})

